import { useEffect, useRef, useState } from "react"
import { toast } from "sonner"

import type { GhlMessage, GhlMessageType } from "@/lib/api/ghl-types"
import { useDecide, useGhlMessages, useGhlUsers, useSendGhlMessage } from "@/lib/api/use-ghl"
import { cx } from "@/lib/utils/cx"
import { suggestedReplies } from "./follow-up"
import { CHANNEL_WORD, ChannelIcon, DARK_BTN, INPUT, StatusPill, fmtDateTime, pill, userName } from "./shared"

// The thread and its composer — one panel, used by Conversations and the
// contact record so a conversation looks the same everywhere. The thread
// scrolls inside; the composer stays in reach.

export interface ThreadPanelProps {
  teamId: number | string
  contactId: string
  conversationId: string | null
  firstName: string
  phone: string | null
  email: string | null
  smsBlocked: boolean
  emailBlocked: boolean
  className?: string
  /** Focus the composer on mount (from a "Text" link). */
  autoFocus?: boolean
  composerRef?: React.RefObject<HTMLTextAreaElement | null>
}

export function ThreadPanel({ teamId, contactId, conversationId, firstName, phone, email, smsBlocked, emailBlocked, className, autoFocus, composerRef }: ThreadPanelProps) {
  const { data: messages = [] } = useGhlMessages(teamId, conversationId)
  const { data: users = [] } = useGhlUsers(teamId)
  const send = useSendGhlMessage(teamId)
  const decide = useDecide(teamId)
  const [type, setType] = useState<GhlMessageType>(phone ? "TYPE_SMS" : "TYPE_EMAIL")
  const [body, setBody] = useState("")
  const [subject, setSubject] = useState("")
  const endRef = useRef<HTMLDivElement>(null)
  const localRef = useRef<HTMLTextAreaElement>(null)
  const ref = composerRef ?? localRef
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }) }, [messages.length])
  useEffect(() => { if (autoFocus) window.setTimeout(() => ref.current?.focus(), 150) }, [autoFocus, ref])

  const blocked = type === "TYPE_SMS" ? !phone || smsBlocked : !email || emailBlocked
  const why = type === "TYPE_SMS" ? (!phone ? "No phone number on file." : "They opted out of texts.") : (!email ? "No email on file." : "They opted out of emails.")
  const lastInbound = [...messages].reverse().find((m) => m.direction === "inbound")
  const suggestions = suggestedReplies(lastInbound?.body, firstName)
  const busy = send.isPending || decide.isPending

  const submit = async () => {
    if (!body.trim() || blocked || busy) return
    try {
      if (conversationId) await send.mutateAsync({ conversationId, type, body: body.trim(), subject: type === "TYPE_EMAIL" ? subject.trim() || null : null })
      else await decide.mutateAsync({ kind: "reply", contactId, body: body.trim(), type })
      setBody(""); setSubject("")
    } catch {
      toast.error("Couldn't send — try again")
    }
  }

  return (
    <div className={cx("flex min-h-[420px] flex-col rounded-xl border border-zinc-200 bg-white", className)}>
      <ol className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {messages.length === 0 && <li className="py-10 text-center text-[13.5px] text-zinc-500">No messages yet. The first one starts the thread.</li>}
        {messages.map((m) => <Bubble key={m.id} m={m} who={firstName} by={m.userId ? userName(users, m.userId) : null} />)}
        <div ref={endRef} />
      </ol>
      <div className="border-t border-zinc-100 p-3">
        {!blocked && type === "TYPE_SMS" && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {suggestions.map((s) => (
              <button key={s} type="button" onClick={() => { setBody(s); ref.current?.focus() }} className={cx("h-8 max-w-full truncate rounded-full border px-3 text-[12.5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30", body === s ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50")}>{s}</button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={() => setType("TYPE_SMS")} disabled={!phone} className={cx(pill(type === "TYPE_SMS"), "h-8 disabled:opacity-40")}>Text</button>
          <button type="button" onClick={() => setType("TYPE_EMAIL")} disabled={!email} className={cx(pill(type === "TYPE_EMAIL"), "h-8 disabled:opacity-40")}>Email</button>
          <span className={cx("ml-auto text-[12px]", blocked ? "font-semibold text-red-700" : "text-zinc-400")}>{blocked ? why : "Sends from the shop's number"}</span>
        </div>
        {type === "TYPE_EMAIL" && !blocked && <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" aria-label="Subject" className={cx(INPUT, "mt-2 w-full")} />}
        <div className="mt-2 flex items-end gap-2">
          <textarea ref={ref} value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") void submit() }} placeholder={blocked ? why : `Write to ${firstName} by ${CHANNEL_WORD[type].toLowerCase()}…`} aria-label="Message" rows={2} disabled={blocked} className={cx(INPUT, "h-auto flex-1 resize-none py-2 disabled:bg-zinc-50")} />
          <button type="button" onClick={() => void submit()} disabled={!body.trim() || busy || blocked} className={DARK_BTN}>{busy ? "Sending…" : "Send"}</button>
        </div>
      </div>
    </div>
  )
}

export function Bubble({ m, who, by }: { m: GhlMessage; who: string; by: string | null }) {
  if (m.type === "TYPE_CALL") {
    const c = m.meta?.call
    return (
      <li className="flex justify-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-100 px-3 py-1 text-[12px] text-zinc-600">
          <ChannelIcon type="TYPE_CALL" className="size-3.5" /> Call · {c?.outcome === "answered" ? `answered · ${Math.round((c.duration ?? 0) / 60)} min` : (c?.outcome ?? "").replace("_", " ")}{by ? ` · ${by}` : ""} · {fmtDateTime(m.dateAdded)}
        </span>
      </li>
    )
  }
  const inbound = m.direction === "inbound"
  return (
    <li className={cx("flex", inbound ? "justify-start" : "justify-end")}>
      <div className="max-w-[78%]">
        <div className={cx("rounded-2xl px-3.5 py-2 text-[14px] leading-relaxed", inbound ? "rounded-bl-md border border-zinc-200 bg-white text-zinc-900" : "rounded-br-md bg-zinc-100 text-zinc-900")}>
          {m.subject && <p className="mb-0.5 text-[12.5px] font-semibold text-zinc-700">{m.subject}</p>}
          <p className="whitespace-pre-wrap">{m.body}</p>
        </div>
        <p className={cx("mt-1 flex items-center gap-1.5 text-[11.5px] text-zinc-400", !inbound && "justify-end")}>
          <ChannelIcon type={m.type} className="size-3" />
          {inbound ? who : by ?? "Automated"} · {fmtDateTime(m.dateAdded)}
          {!inbound && <StatusPill status={m.status} />}
        </p>
      </div>
    </li>
  )
}
